import { useState } from 'react';
import { Stack, Button, Group } from '@mantine/core';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import {
    FormikTextInput,
    PlatformFieldset,
    FormWithButtons
} from '@flexinet/ui-components';
import { BillingProvidersDropdown } from "../../billing-providers/inputs/BillingProvidersDropdown.tsx";
import { BillingProviderPropertyCard } from './BillingProviderPropertyCard';
import { Customer } from '../types';
import { useCustomer } from '../hooks/useCustomer';

interface BillingProviderManagementProps {
    customer: Customer;
    onCustomerUpdate?: (updatedCustomer: Customer) => void;
}

const BillingProviderSchema = Yup.object().shape({
    billingProviderId: Yup.string().optional(),
    billingProviderRecordId: Yup.string().optional(),
});

export const BillingProviderManagement = ({ customer, onCustomerUpdate }: BillingProviderManagementProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [currentCustomer, setCurrentCustomer] = useState(customer);
    const { updateRecord } = useCustomer(customer.id);

    const handleSubmit = async (values: { billingProviderId?: string; billingProviderRecordId?: string }) => {
        try {
            const updatedCustomer = {
                ...currentCustomer,
                billingInfo: {
                    ...currentCustomer.billingInfo,
                    billingProviderId: values.billingProviderId,
                    billingProviderRecordId: values.billingProviderRecordId
                }
            };
            
            updateRecord(updatedCustomer);
            setCurrentCustomer(updatedCustomer);
            if (onCustomerUpdate) {
                onCustomerUpdate(updatedCustomer);
            }
            setIsEditing(false);
        } catch (error) {
            console.error('Failed to update customer billing provider:', error);
        }
    };

    const handleRecordUpdate = (updates: Partial<Customer>) => {
        const updated = { ...currentCustomer, ...updates };
        setCurrentCustomer(updated);
        if (onCustomerUpdate) {
            onCustomerUpdate(updated);
        }
    };

    if (isEditing) {
        return (
            <Formik
                initialValues={{
                    billingProviderId: currentCustomer.billingInfo?.billingProviderId || '',
                    billingProviderRecordId: currentCustomer.billingInfo?.billingProviderRecordId || ''
                }}
                validationSchema={BillingProviderSchema}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, isSubmitting }) => (
                    <FormWithButtons 
                        handleSubmit={handleSubmit} 
                        isSubmitting={isSubmitting}
                        handleCancel={() => setIsEditing(false)}
                        buttonsPosition="bottom"
                    >
                        <PlatformFieldset legend="Billing Provider">
                            <Field
                                name="billingProviderId"
                                label="Billing Provider"
                                component={BillingProvidersDropdown}
                                my="xl"
                            />
                            <Field
                                name="billingProviderRecordId"
                                label="Billing Provider Record ID"
                                description="Please enter the billing provider record ID"
                                component={FormikTextInput}
                                my="xl"
                            />
                        </PlatformFieldset>
                    </FormWithButtons>
                )}
            </Formik>
        );
    }

    return (
        <Stack gap="md">
            <Group justify="flex-end">
                <Button size="xs" variant="light" onClick={() => setIsEditing(true)}>
                    Edit Billing Provider
                </Button>
            </Group>
            <BillingProviderPropertyCard 
                record={currentCustomer} 
                onRecordUpdate={handleRecordUpdate}
            />
        </Stack>
    );
};